export function isOwnerOrAdmin(user) {
  return ['agency_owner', 'agency_admin'].includes(user?.role);
}

export function canAccessCommercials(user) {
  return isOwnerOrAdmin(user) || isFinanceUser(user);
}

export function getSalesRoleValue(user) {
  if (!user) return '';
  if (isOwnerOrAdmin(user)) return 'sales_manager';
  if (['sales_manager', 'sales_executive', 'lead_generator'].includes(user.sales_role)) return user.sales_role;
  if (['sales_manager', 'sales_executive', 'lead_generator'].includes(user.role)) return user.role;
  return '';
}

export function isFinanceUser(user) {
  const roles = new Set(['finance', 'commercials', 'commercials_manager', 'finance_manager']);
  return user?.access_role === 'finance' || roles.has(user?.role) || roles.has(user?.finance_role) || roles.has(user?.commercials_role);
}

export function isProjectManager(user) {
  return user?.access_role === 'project_manager' || ['project_manager', 'account_manager'].includes(user?.delivery_role) || user?.role === 'project_manager';
}

export function isTeamMember(user) {
  return user?.access_role === 'team_member' || ['team_member', 'editor', 'designer', 'copywriter', 'developer'].includes(user?.delivery_role) || user?.role === 'team_member' || (user?.role === 'agency_editor' && !user?.sales_role && !user?.finance_role && !user?.commercials_role && !user?.hrms_role && !user?.hr_role && !user?.employee_role && !user?.delivery_role);
}

export function isEmployeeUser(user) {
  return user?.access_role === 'employee' || ['employee', 'agency_employee', 'staff'].includes(user?.role) || user?.employee_role === 'employee';
}

export function getRoleHomePath(user) {
  if (isOwnerOrAdmin(user)) return "/dashboard";
  const explicitSalesRole = user?.access_role && ['lead_generator', 'sales_executive', 'sales_manager'].includes(user.access_role) ? user.access_role : getSalesRoleValue(user);
  if (explicitSalesRole === 'lead_generator') return '/lead-generator-dashboard';
  if (explicitSalesRole === 'sales_executive') return '/sales-executive-dashboard';
  if (explicitSalesRole === 'sales_manager' && !isOwnerOrAdmin(user)) return '/sales';
  if (isReviewer(user)) return '/reviews';
  if (canAccessHRMS(user)) return '/hrms';
  if (isFinanceUser(user)) return '/finance-dashboard';
  if (isProjectManager(user)) return '/project-manager-dashboard';
  if (isTeamMember(user)) return '/team-member-workbench';
  if (isEmployeeUser(user)) return '/employee-dashboard';
  if (user && !isOwnerOrAdmin(user) && user.role === 'agency_editor') return '/team-member-workbench';
  return '/dashboard';
}

export function getRoleDashboardLabel(user) {
  const path = getRoleHomePath(user);
  const labels = {
    '/lead-generator-dashboard': 'Lead Generator Dashboard',
    '/sales-executive-dashboard': 'Sales Executive Dashboard',
    '/finance-dashboard': 'Finance Dashboard',
    '/project-manager-dashboard': 'Project Manager Dashboard',
    '/team-member-workbench': 'Team Member Workbench',
    '/employee-dashboard': 'Employee Dashboard',
    '/dashboard': 'Dashboard',
    '/sales': 'Sales Dashboard',
  };
  return labels[path] || 'Dashboard';
}

export function canManagePayroll(user) {
  const roles = [user?.role, user?.access_role, user?.hrms_role, user?.hr_role];
  return roles.some(role => ['agency_owner', 'agency_admin', 'hr_manager', 'hr_admin'].includes(role)) || [...(user?.permissions || []), ...(user?.access?.permissions || [])].includes('hrms.manage_payroll');
}

export function canAccessHRMS(user) {
  return canManagePayroll(user) || [user?.role, user?.hrms_role, user?.hr_role].some(role => role === 'hr_executive') || [...(user?.permissions || []), ...(user?.access?.permissions || [])].some(p => p.startsWith('hrms.'));
}

export function isReviewer(user) {
  return user?.review_role === "reviewer" || user?.role === "reviewer";
}

export function canAccessAppPath(user, pathname) {
  if (isOwnerOrAdmin(user)) return true;
  if (!user || ['client_user', 'client', 'client_portal', 'portal_client'].includes(user.access_role || user.role)) return false;
  const module = pathname.split('/')[1];
  const sales = Boolean(getSalesRoleValue(user)) || ['sales_manager', 'sales_executive', 'lead_generator'].includes(user?.role);
  const delivery = isProjectManager(user) || isTeamMember(user) || isEmployeeUser(user) || isReviewer(user);
  if ((pathname === getRoleHomePath(user) && pathname !== '/dashboard') || module === 'pricing') return true;
  if (module === 'hrms') return pathname.startsWith('/hrms/payroll') ? canManagePayroll(user) : canAccessHRMS(user);
  if (module === 'sales' && /^\/sales\/(settings|team)(\/|$)/.test(pathname)) return getSalesRoleValue(user) === 'sales_manager';
  if (module === 'sales' && /^\/sales\/(proposals|quotations|deals)(\/|$)/.test(pathname) && getSalesRoleValue(user) === 'lead_generator') return false;
  if (module === 'sales' || module === 'lead-generator-dashboard' || module === 'sales-executive-dashboard') return sales;
  if (module === 'commercials' || module === 'finance-dashboard') return isFinanceUser(user);
  if (['settings', 'webhooks', 'dashboard'].includes(module)) return false;
  if (module === 'team') return isProjectManager(user) || canAccessHRMS(user);
  if (module === 'projects' && /(?:team-allocation|\/allocation)(?:\/|$)/.test(pathname)) return isProjectManager(user);
  if (module === 'clients') return sales || isFinanceUser(user) || isProjectManager(user);
  if (['paperwork', 'contracts'].includes(module)) return sales || isProjectManager(user);
  if (module === 'reports' || module === 'analytics') return sales || isFinanceUser(user) || isProjectManager(user) || isTeamMember(user);
  if (['projects', 'deliverables', 'reviews', 'calendar', 'templates', 'campaigns', 'team-member-workbench', 'employee-dashboard', 'project-manager-dashboard'].includes(module)) return delivery;
  return false;
}
